import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { X, ArrowRight, Check, Compass } from 'lucide-react';
import { useTour } from './TourContext';
import { TOURS } from './tour-definitions';

/**
 * Floating tour card + element spotlight. Mounted once inside AppLayout.
 * On each step it navigates (if the step has a route), then polls for the
 * step's data-tour anchor and draws a pulsing ring around it. When the anchor
 * isn't on screen (e.g. "open one of YOUR properties" — we can't know which),
 * the card alone guides the user; the ring appears as soon as the target
 * renders. Repositioning runs on a light interval — the CRM scrolls inside
 * <main>, so window scroll events don't fire reliably.
 */
export const TourGuide: React.FC<{ theme?: 'light' | 'dark' }> = ({ theme = 'dark' }) => {
    const isLight = theme !== 'dark';
    const { tour, nextStep, stopTour } = useTour();
    const navigate = useNavigate();
    const location = useLocation();
    const [rect, setRect] = useState<DOMRect | null>(null);

    const steps = tour ? TOURS[tour.key] : null;
    const step = steps ? steps[tour!.step] : null;

    // Navigate when the step declares a route we're not on.
    useEffect(() => {
        if (!step?.route) return;
        if (location.pathname !== step.route) navigate(step.route);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tour?.key, tour?.step]);

    // Track the anchor element's rect while the step is active.
    useEffect(() => {
        setRect(null);
        if (!step?.anchor) return;
        let cancelled = false;
        let scrolled = false;
        const measure = () => {
            if (cancelled) return;
            const el = document.querySelector(`[data-tour="${step.anchor}"]`) as HTMLElement | null;
            if (el) {
                if (!scrolled) {
                    scrolled = true;
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                setRect(el.getBoundingClientRect());
            } else {
                setRect(null);
            }
        };
        measure();
        const id = window.setInterval(measure, 350);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [tour?.key, tour?.step, step?.anchor, location.pathname]);

    if (!tour || !steps || !step) return null;

    const isLast = tour.step === steps.length - 1;

    return createPortal(
        <>
            {/* Spotlight ring over the anchor */}
            {rect && (
                <div
                    className="fixed z-[9998] pointer-events-none rounded-2xl border-2 border-amber-400 animate-pulse"
                    style={{
                        top: rect.top - 6,
                        left: rect.left - 6,
                        width: rect.width + 12,
                        height: rect.height + 12,
                        boxShadow: '0 0 0 4px rgba(245,158,11,0.25), 0 0 24px rgba(245,158,11,0.35)',
                    }}
                />
            )}

            {/* Guide card */}
            <div className={clsx(
                'fixed z-[9999] bottom-5 right-5 left-5 sm:left-auto sm:w-[380px] rounded-2xl border shadow-2xl p-5',
                isLight ? 'bg-white border-amber-200 shadow-amber-500/10' : 'bg-slate-900 border-amber-500/30 shadow-black/50'
            )}>
                <div className="flex items-start justify-between gap-3 mb-1.5">
                    <span className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-amber-500">
                        <Compass size={13} /> Guía · Paso {tour.step + 1} de {steps.length}
                    </span>
                    <button
                        onClick={stopTour}
                        title="Salir de la guía"
                        aria-label="Salir de la guía"
                        className={clsx('p-1 -m-1 rounded-lg transition-colors', isLight ? 'text-slate-300 hover:text-slate-500' : 'text-slate-600 hover:text-slate-300')}
                    >
                        <X size={15} />
                    </button>
                </div>
                <h4 className={clsx('font-black text-base mb-1', isLight ? 'text-slate-900' : 'text-white')}>{step.title}</h4>
                <p className={clsx('text-sm leading-relaxed mb-4', isLight ? 'text-slate-600' : 'text-slate-300')}>{step.body}</p>
                <div className="flex items-center justify-between">
                    <div className="flex gap-1.5">
                        {steps.map((_, i) => (
                            <span key={i} className={clsx('w-1.5 h-1.5 rounded-full', i <= tour.step ? 'bg-amber-500' : isLight ? 'bg-slate-200' : 'bg-white/15')} />
                        ))}
                    </div>
                    <button
                        onClick={nextStep}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-black uppercase tracking-wide transition-colors"
                    >
                        {isLast ? <>Listo <Check size={14} /></> : <>Siguiente <ArrowRight size={14} /></>}
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};
